import React, { useMemo } from 'react'
import { Box, Center, HStack, IconButton, Text } from '@chakra-ui/react'
import {
  useCallDurationLabel,
  useContactCenterModalEvents,
  useContactCenterModalState,
  useSelectedResidentContact,
} from './contact-center.hooks'
import { MODALSTATE } from './contact-center.store'
import { truncateWithEllipsis } from 'utils/string-formatters'
import { maskPhoneNumber } from './contact-center.utils'
import { useDialerCallStatus, useDialerState, useTwilioCall } from './dialer/dialer.hooks'
import { BsFillMicFill, BsFillMicMuteFill, BsTelephoneFill } from 'react-icons/bs'

export interface ContactCenterTriggerProps {}

const ContactCenterTrigger: React.FC<ContactCenterTriggerProps> = props => {
  const { modalState } = useContactCenterModalState()
  const { open: openContactCener } = useContactCenterModalEvents()
  const { selected } = useSelectedResidentContact()
  const { dialerState } = useDialerState()
  const { status: callStatus } = useDialerCallStatus()
  const { label: callDuration } = useCallDurationLabel()
  const { muteCall, endCurrentCall } = useTwilioCall()

  const fullName = useMemo(() => {
    return truncateWithEllipsis(`${selected?.firstName} ${selected?.lastName}`, 40)
  }, [selected])

  const handleClick = () => {
    openContactCener()
  }

  const handleEndCall = () => {
    endCurrentCall()
  }

  const handleMute = () => {
    muteCall()
  }

  if (modalState !== MODALSTATE.HIDE) return null

  if (dialerState.isCalling) {
    return (
      <Box position="relative" bg="#fff" width="260px" height="37px" borderRadius="10px">
        <HStack>
          <Box ml="10px">
            <Text fontSize="12px" fontWeight="600" color="#000">
              {fullName}
            </Text>
            <Text color="gray.700" fontSize="10px" fontWeight="400">
              {selected && maskPhoneNumber(selected.phoneNumber)}
            </Text>
          </Box>
          <Box w="15%">
            <Center>
              <Text fontSize="12px" color="gray.700" fontWeight="400">
                {callDuration}
              </Text>
            </Center>
          </Box>
          <Box>
            <HStack>
              <IconButton
                backgroundColor="red"
                zIndex="10"
                aria-label="end-phone-dialer"
                sx={{
                  svg: {
                    transform: 'rotate(135deg) !important',
                  },
                }}
                icon={<BsTelephoneFill fontSize="12px" />}
                onClick={handleEndCall}
                _hover={{
                  backgroundColor: 'red',
                  opacity: 0.8,
                  transform: 'scale(0.8)',
                }}
                _active={{
                  backgroundColor: 'red',
                  opacity: 0.8,
                  transform: 'scale(0.8)',
                }}
                minWidth="26px"
                maxW="26px"
                minH="26px"
                maxH="26px"
                borderRadius="20px"
              />
              <IconButton
                minWidth="26px"
                maxW="26px"
                minH="26px"
                maxH="26px"
                variant="solid"
                colorScheme="brand"
                aria-label="expand"
                icon={
                  callStatus.mute ? (
                    <BsFillMicMuteFill fontSize="12px" color="#fff" />
                  ) : (
                    <BsFillMicFill fontSize="12px" color="#fff" />
                  )
                }
                onClick={handleMute}
                _hover={{
                  opacity: 0.8,
                  transform: 'scale(0.8)',
                }}
                _active={{
                  opacity: 0.8,
                  transform: 'scale(0.8)',
                }}
                borderRadius="20px"
              />
            </HStack>
          </Box>
        </HStack>

        <IconButton
          minWidth="12px"
          maxW="12px"
          minH="12px"
          maxH="12px"
          variant="unstyled"
          position="absolute"
          top="8%"
          left="89%"
          aria-label="expand"
          icon={<ExpandIcon />}
          onClick={handleClick}
        />
      </Box>
    )
  }

  return (
    <Box position="relative" bg="#fff" width="144px" height="34px" borderRadius="10px">
      <Box ml="10px">
        <Text fontSize="12px" fontWeight="600" color="#000">
          {fullName}
        </Text>
        <Text color="gray.700" fontSize="10px" fontWeight="400">
          {selected && maskPhoneNumber(selected.phoneNumber)}
        </Text>
      </Box>

      <IconButton
        minWidth="12px"
        maxW="12px"
        minH="12px"
        maxH="12px"
        variant="unstyled"
        position="absolute"
        top="8%"
        left="89%"
        aria-label="expand"
        icon={<ExpandIcon />}
        onClick={handleClick}
      />
    </Box>
  )
}

const ExpandIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="13" viewBox="0 0 11 13" fill="none">
      <path
        d="M6.46132 4.5732C6.51051 4.57317 6.55916 4.58343 6.60414 4.60332C6.64912 4.62322 6.68944 4.65231 6.72251 4.68872C6.87077 4.84044 6.86809 5.07292 6.71637 5.22118L1.78561 10.0322C1.63389 10.1804 1.40141 10.1778 1.25315 10.0261C1.10489 9.87433 1.10757 9.64185 1.25928 9.49359L6.19013 4.67508C6.26599 4.60095 6.36392 4.56458 6.45391 4.56562L6.46132 4.5732Z"
        fill="#4A5568"
      />
      <path
        d="M3.4614 4.53851L6.4612 4.57308C6.67119 4.5755 6.83428 4.74239 6.83186 4.95237L6.79729 7.95217C6.79487 8.16216 6.62798 8.32525 6.41799 8.32283C6.20801 8.32041 6.04492 8.15352 6.04734 7.94353L6.07759 5.31871L3.45276 5.28846C3.24277 5.28604 3.07969 5.11915 3.08211 4.90916C3.08453 4.69918 3.25142 4.53609 3.4614 4.53851Z"
        fill="#4A5568"
      />
      <path
        d="M1.9968 1.52168L8.74635 1.59945C9.36881 1.60662 9.86549 2.11488 9.85832 2.73734L9.78054 9.48689C9.77337 10.1093 9.26511 10.606 8.64265 10.5989L4.8929 10.5556C4.68292 10.5532 4.51983 10.3863 4.52225 10.1763C4.52467 9.96636 4.69156 9.80327 4.90154 9.80569L8.65129 9.8489C8.86128 9.85132 9.02817 9.68824 9.03059 9.47825L9.10837 2.7287C9.11079 2.51871 8.9477 2.35182 8.73771 2.3494L1.98816 2.27163C1.77817 2.26921 1.61128 2.43229 1.60886 2.64228L1.56566 6.39203C1.56324 6.60202 1.39635 6.7651 1.18636 6.76268C0.976374 6.76027 0.813286 6.59337 0.815706 6.38339L0.858915 2.63364C0.866087 2.01118 1.37434 1.5145 1.9968 1.52168Z"
        fill="#4A5568"
      />
    </svg>
  )
}

export default ContactCenterTrigger
